import { LoadingButton } from '@mui/lab';
import _ from 'lodash';
import {
  ReactElement,
  Suspense,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { useSetRecoilState, useRecoilState, useRecoilValue } from 'recoil';

import { createOrder } from '@app/adapter/order-service';
import { CreateConfirmModal } from '@app/components/Order/CreateConfirmModal';
import { ProductDetailInfo } from '@app/components/Product/ProductDetailInfo';
import { BottomAppBar } from '@app/components/Shared/BottomAppBar';
import { HeadBlock } from '@app/components/Shared/HeadBlock';
import { Loading } from '@app/components/Shared/Loading';
import { TopNavPortal } from '@app/components/TopNav/TopNavPortal';
import {
  afterLoginRouteSelector,
  loggedInUserState,
  userAuthInfoSelector,
} from '@app/domain/app';
import { searchConditionState } from '@app/domain/catalog';
import { getProduct, getUserOrders } from '@app/domain/network-actions';
import { orderCountState } from '@app/domain/top-nav';
import { useSetSnackbar } from '@app/hooks/useSetSnackbar';
import { CategoryName, Product, ProductType } from '@app/types/catalog';
import {
  OrderCancelType,
  OrderCreation,
  OrderCustomerCreation,
  OrderCustomFields,
  OrderItemCreation,
  Order,
  OrderStatus,
} from '@app/types/order';
import { getSearchResultUrl } from '@app/utils/catalog';
import { isError } from '@app/utils/error';
import { isUserInfoRegistered } from '@app/utils/user';

export function Show(): ReactElement {
  const { productId } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const setSnackbar = useSetSnackbar();
  const authInfo = useRecoilValue(userAuthInfoSelector);
  const loggedInUser = useRecoilValue(loggedInUserState);
  const setAfterLoginRoute = useSetRecoilState(afterLoginRouteSelector);
  const conditionState = useRecoilValue(searchConditionState);
  const [navBadgeCount, setNavBadgeCount] = useRecoilState(orderCountState);
  const [organizationId, setOrganizationId] = useState<string>('');
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [product, setProduct] = useState<Product | null>(null);
  const [order, setOrder] = useState<Order | null | undefined>();
  const [isConfirmModal, setIsConfirmModal] = useState(false);

  const orderButtonLabel = useMemo(() => {
    if (!authInfo) return 'ログイン・会員登録して応募する';
    if (!isUserInfoRegistered(loggedInUser))
      return '会員情報を登録して応募する';
    if (order) return '応募済み';
    return '応募する';
  }, [authInfo, loggedInUser, order]);

  const isDisabledOrder = useMemo(() => {
    return !!(
      !isUserInfoRegistered(loggedInUser) ||
      (!isLoading && product && organizationId && order === null)
    );
  }, [isLoading, loggedInUser, order, organizationId, product]);

  const fetchProduct = useCallback(async () => {
    if (!productId) return;
    try {
      const result = await getProduct(productId);
      setProduct(result.data);
      setOrganizationId(result.data.organizationId);
    } catch (e) {
      if (isError(e)) {
        console.error('fetchProduct', e.message);
      }
      setSnackbar(true, '求人が見つかりません');
    }
  }, [productId, setSnackbar]);

  const fetchOrders = useCallback(async () => {
    if (!authInfo?.accessToken || !productId) return;
    try {
      const data = await getUserOrders(authInfo.accessToken, authInfo.userId, {
        productId,
      }).then((v) => v.data);
      setOrder(
        data.value.filter((o) => o.status !== OrderStatus.CANCELED)[0] || null
      );
    } catch (e) {
      if (isError(e)) {
        console.error('fetchOrders', e.message);
      }
    }
  }, [authInfo, productId]);

  useEffect(() => {
    void fetchProduct();
    void fetchOrders();
    // eslint-disable-next-line
  }, []);

  const handleGoBack = useCallback(() => {
    navigate(getSearchResultUrl(conditionState));
  }, [conditionState, navigate]);

  const handleClickOrder = useCallback(() => {
    if (!authInfo) {
      setAfterLoginRoute(location.pathname);
      navigate('/login');
      return;
    }
    if (!loggedInUser?.customFields?.familyName) {
      setAfterLoginRoute(location.pathname);
      navigate('/profile/edit');
      return;
    }
    setIsConfirmModal(true);
  }, [authInfo, location.pathname, loggedInUser, navigate, setAfterLoginRoute]);

  const handleOrderCreate = useCallback(async () => {
    if (!isDisabledOrder || !loggedInUser || !authInfo) return;
    setIsLoading(true);

    const customer: OrderCustomerCreation = {
      addressLine1: loggedInUser.addressLine1,
      age: '',
      email: loggedInUser.email,
      name: `${loggedInUser.customFields.familyName}${loggedInUser.customFields.firstName}`,
      nameKana: `${loggedInUser.customFields.familyNameKana}${loggedInUser.customFields.firstNameKana}`,
      phone: loggedInUser.phoneNumber,
      preferredContactMethod: '',
      preferredTimeToContact: '',
      user: authInfo.userId,
    };
    const items: OrderItemCreation[] = [
      {
        productId: productId || '',
        quantity: 1,
        variantId: product?.variants[0].id,
      },
    ];
    const customFields: OrderCustomFields = {
      cancelMessage: '',
      cancelRequestAt: '',
      cancelType: OrderCancelType.NOT_CANCEL,
      productDay: product?.customFields?.day || '',
      productType:
        product?.category?.name === CategoryName.SPOT
          ? ProductType.SPOT
          : ProductType.REGULAR_PART_TIME,
      workResult: '',
      workResultAt: '',
    };
    const payload: OrderCreation = {
      customFields,
      customer,
      items,
    };

    try {
      const result = await createOrder(
        authInfo.accessToken,
        authInfo.fingerPrint,
        organizationId,
        payload
      );
      setOrder(result.data);
      setSnackbar(
        true,
        '応募しました。医療機関からの返信をお待ち下さい',
        'success'
      );
      setNavBadgeCount(navBadgeCount + 1);
    } catch (e) {
      if (isError(e)) {
        console.error('handleOrderCreate', e.message);
      }
      setSnackbar(true, '応募に失敗しました');
    } finally {
      setIsLoading(false);
      setIsConfirmModal(false);
    }
  }, [
    authInfo,
    isDisabledOrder,
    loggedInUser,
    navBadgeCount,
    organizationId,
    product,
    productId,
    setSnackbar,
    setNavBadgeCount,
  ]);

  return (
    <>
      <HeadBlock />
      <TopNavPortal
        title={authInfo ? _.get(product, 'organization.name') : ''}
        onGoBack={handleGoBack}
      />
      {product && (
        <Suspense fallback={<Loading />}>
          <ProductDetailInfo product={product} />
        </Suspense>
      )}
      <BottomAppBar
        children={
          <LoadingButton
            variant="contained"
            color="black"
            loading={isLoading}
            disabled={!isDisabledOrder}
            onClick={handleClickOrder}
            sx={{ flex: '1 1 0' }}
            fullWidth
          >
            {orderButtonLabel}
          </LoadingButton>
        }
      />
      <CreateConfirmModal
        open={isConfirmModal}
        onClose={() => setIsConfirmModal(false)}
        onSubmit={handleOrderCreate}
      />
    </>
  );
}
